import React, {Dispatch, SetStateAction} from "react";
import {AllStates, Level, NON_JSON_ERROR_PREFIX, StepForm, TranslateFunction} from '../Forms';

import {JSONSchema7} from "json-schema";
import schema from "./mappings.schema.json"
import {Mapping} from "./Mappings";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import {Typography} from "@mui/material";
import {I18nMessage} from "../Representation";
import {activeApplicantModuleMappingsByProgram, getApplicableRequirementsDefinition} from "./MappingsPage";
import Ajv, {ErrorObject} from "ajv";
import {getDebug} from "../Data";

export const EMPTY_MAPPINGS: Mapping[] = [];

function getRequiredModuleKeysWithMapping(allStates: AllStates) {
    return new Set(
        activeApplicantModuleMappingsByProgram(allStates)
            // create a sequence of all mappings without structuring them by degree program
            .flatMap(mappingsList => mappingsList)
            // create a sequence of all proportions of all mappings
            .flatMap(mapping => mapping.proportions)
            // create a sequence of all required module keys to which something is mapped
            .filter(proportion => proportion.applicant_credits > 0)
            .map(proportion => proportion.required_module_key));
}

export function getSubjectSpecificEligibilityInfo(allStates: AllStates) {
    const warningMessages: I18nMessage[] = [];
    const infoMessages: I18nMessage[] = [];

    const requirements = getApplicableRequirementsDefinition(allStates);
    const requiredModuleKeysWithMapping = getRequiredModuleKeysWithMapping(allStates);

    let excess = 0;

    const INSUFFICIENT_MAPPING = "insufficient_mapping";
    requirements.rules.forEach(rule => {
        if (rule.kind === "some_of") {
            // some of the modules in keys must be completed fully (i.e., in sum, the converted credits must be at least the amount specified for the module)
            // the limit specifies how many modules must be covered at least
            // for the simplified check here do not consider the credit points
            // warn if there is not any mapping for at least the required amount of modules
            const keysWithMapping = rule.keys.filter(key => {
                // For example for Data Science, there are multiple ways to fulfill a required module.
                // In that case, "analysis" can either be covered by "Basic Real Analysis" or by "Analysis I".
                // Therefore, in general "key" is a comma separated list of keys.
                // There is a mapping to a list of keys if there is a mapping to at least one of the sub-keys.
                return key.split(",").some(subKey => requiredModuleKeysWithMapping.has(subKey));
            });
            if (keysWithMapping.length < rule.limit) {
                if (!warningMessages.find(msg => msg === INSUFFICIENT_MAPPING))
                    warningMessages.push(INSUFFICIENT_MAPPING);
            } else {
                if (rule.count_excess)
                    excess += keysWithMapping.length - rule.limit;
            }
        } else if (rule.kind === "all_of" || rule.kind === "all_of_partial") {
            // all_of: all of the modules in keys must be completed fully (i.e., in sum, the converted credits must be at least the amount specified for the module)
            // all_of_partial: all of the modules in keys must be completed at least partially (i.e., to any amount of credit points)
            // limit can be ignored
            // for the simplified check here do not consider the credit points, therefore, both rules can be checked in the same way
            // warn it there is not any mapping for at least the required amount of modules
            if (rule.keys.some(key => {
                // For example for Data Science, there are multiple ways to fulfill a required module.
                // In that case, "analysis" can either be covered by "Basic Real Analysis" or by "Analysis I".
                // Therefore, in general "key" is a comma separated list of keys.
                // There is no mapping to a list of keys if there is no mapping to any of the sub-keys.
                return key.split(",").every(subKey => !requiredModuleKeysWithMapping.has(subKey));
            })) {
                if (!warningMessages.find(msg => msg === INSUFFICIENT_MAPPING))
                    warningMessages.push(INSUFFICIENT_MAPPING);
            }
        }
    });

    excess -= requirements.excessOffset;

    const eligibilityPoints = warningMessages.length > 0 ? 0 :
        excess >= 2 ? 35 : excess >= 1 ? 20 : 0;

    if (getDebug())
        infoMessages.push("Eligibility points at most " + eligibilityPoints);

    return {warningMessages: warningMessages, infoMessages: infoMessages, eligibilityPoints: eligibilityPoints};
}

export class MappingsForm extends StepForm {

    constructor() {
        super(schema as JSONSchema7);
    }

    getFormNs(): string | undefined {
        return undefined;
    }

    getTitleKey() {
        return "mappings_title"
    };

    getPage([formData, setFormData]: [any, Dispatch<SetStateAction<any>>],  {modules: [modules,]}: AllStates, _t: TranslateFunction, _lang: string) {
        return (<React.Fragment>
            {
                modules.flatMap(moduleList => moduleList.modules)
                    .map(module => {
                        return <Card>
                            <CardHeader title={<Typography>{module.title}</Typography>}/>
                        </Card>;

                    })
            }
        </React.Fragment>);
    }

    validate(newAjv: () => Ajv, data: any, t: TranslateFunction, allStates : AllStates): ErrorObject[] {
        const errors = super.validate(newAjv, data, t, allStates);

        const {modules: [modules, ], mappings: [mappings, ]} = allStates;

        const incompletelyMappedModules = modules.flatMap(moduleList => {
            return moduleList.modules.filter(module => {
                const mapping = mappings.find(mapping =>
                    mapping.applicant_module_degree_program_index === moduleList.degree_program_index &&
                    mapping.applicant_module_title === module.title);

                // if there is a mapping, include the module if not all credits are mapped
                if (mapping) {
                    const mappedCredits = mapping.mapped_required_modules.map(
                        proportion => proportion.applicant_credits).
                    reduce((acc, curr) => (acc + curr), 0);
                    return mappedCredits < module.credits;
                } else {
                    // if there is no mappiing, include the module
                    return true;
                }
            })
        });

        if (incompletelyMappedModules.length > 0) {
            const keyword = NON_JSON_ERROR_PREFIX + "not_all_mapped"
            const params = {
                incompleteModules: incompletelyMappedModules.map(module => module.title).join(", ")
            }
            const defaultMsg = "Not all modules are fully mapped. {incompleteModules}"
            errors.push({
                keyword: keyword,
                instancePath: "/",
                schemaPath: "#/" + keyword,
                params: params,
                message: t("ajv_error." + keyword, defaultMsg, params)
            });
        }
        return errors;
    }

    getContextMessageKeys = (_data: any, allStates: AllStates): [I18nMessage[], I18nMessage[]] => {
        const {warningMessages: warningMessages, infoMessages: infoMessages} = getSubjectSpecificEligibilityInfo(allStates);

        return [warningMessages, infoMessages];
    }

    getValidationMessageKeys(data: any, allStates: AllStates, t: TranslateFunction): null | {
        level: Level;
        messages: string[]
    } {
        const requiredModuleKeysWithMapping = getRequiredModuleKeysWithMapping(allStates);

        if (requiredModuleKeysWithMapping.size === 0) {
            return {
                level: Level.INCOMPLETE,
                messages: ["no_mappings"]
            };
        }

        return super.getValidationMessageKeys(data, allStates, t);
    }

    getExplanationMarkdownKey() {
        return "mappings.explanation.markdown";
    }

    getHelpUrl(): string {
        return "faq.html#mappings";
    }

}